import React, {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
} from 'react';
import './erreurs.css';

import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  getFilteredRowModel,
  getSortedRowModel,
} from '@tanstack/react-table';

import { useVirtualizer } from '@tanstack/react-virtual';

import { sortBy, join, remove } from 'lodash';

// A TanStack fork of Kent C. Dodds' match-sorter library that provides ranking information
import { rankings, rankItem } from '@tanstack/match-sorter-utils';

import { GlobalContext } from '../../utils/globalprovider.js';
import DebouncedInput from './components/debounced-input.js';
import EditableCell from './components/editable-cell.js';
import SortIcon from './components/sort-icon.js';

import SimplePopup from '../bases/popup/simplePopup.js';
import IndeterminateCheckbox from './components/indeterminate-checkbox.js';

// Define a custom fuzzy filter function that will apply ranking info to rows (using match-sorter utils)
const fuzzyFilter = (row, columnId, value, addMeta) => {
  // Rank the item
  const itemRank = rankItem(row.getValue(columnId), value, {
    threshold: rankings.CONTAINS,
  });

  // Store the itemRank info
  addMeta({
    itemRank,
  });

  // Return if the item should be filtered in/out
  return itemRank.passed;
};


// https://tanstack.com/table/latest/docs/guide/column-defs

const BaseErreurs = forwardRef((props, ref) => {
  const context = useContext(GlobalContext);
  const lang = context.language;

  const [data, setData] = React.useState(() => []);
  const [globalFilter, setGlobalFilter] = React.useState('');
  const [popup, setPopup] = React.useState({
    display: false,
    title: '',
    text: '',
    buttons: [],
    showIcon: false,
    icon: null,
  });

  const onClosePopup = () => {
    setPopup({
      display: false,
      title: '',
      text: '',
      buttons: [],
      showIcon: false,
      icon: null,
    });
  };

  const deleteItem = (index) => {
    data.splice(index, 1);
    setData(data);
  };

  useImperativeHandle(ref, () => ({
    setCommonErrors(blob) {
      const list = blob.map((row, index) => {
        return {
          index,
          value1: row[0],
          value2: row[1],
          value3: row[2] || '',
          value4: row[3] || '',
        };
      });
      setData(list);
    },
    add() {
      // on évite les doublons de ligne vide
      const firstItem = data[0];
      if (
        firstItem.value1 === '' &&
        firstItem.value2 === '' &&
        firstItem.value3 === '' &&
        firstItem.value4 === ''
      ) {
        return;
      }

      const items = [...data];
      items.unshift({
        value1: '',
        value2: '',
        value3: '',
        value4: '',
      });

      setData(items);

      rowVirtualizer.scrollToIndex(0);
    },
    delete() {
      const rows = table.getSelectedRowModel().rows;

      if (rows.length === 0) {
        setPopup({
          display: true,
          title: '',
          text: {
            fr: `Vous devez d'abord sélectionner des éléments à supprimer.`,
            en: 'You must first select the items to be deleted.',
          },
          onCancel: onClosePopup,
          buttons: [],
          showIcon: true,
          icon: null,
        });

        return;
      }

      setPopup({
        display: true,
        title: '',
        text: {
          fr: `Voulez-vous supprimer ${rows.length} ${
            rows.length === 1 ? 'élément' : 'éléments'
          } ?`,
          en: `Do you want to delete ${rows.length} ${
            rows.length === 1 ? 'item' : 'items'
          }?`,
        },
        buttons: [
          {
            text: {
              fr: 'Supprimer',
              en: 'Delete',
            },
            onclick: this.confirmDeleteItems,
          },
          {
            text: {
              fr: 'Annuler',
              en: 'Cancel',
            },
            onclick: this.cancelDeleteItems,
          },
        ],
        onCancel: this.cancelDeleteItems,
        showIcon: true,
        icon: null,
      });
    },
    confirmDeleteItems() {
      const rowsToDelete = table
        .getSelectedRowModel()
        .rows.map((x) => x.original);

      const items = remove(data, (row) => {
        return !rowsToDelete.includes(row);
      });

      setData(items);
      table.resetRowSelection();
      onClosePopup();
    },
    cancelDeleteItems() {
      table.resetRowSelection();
      onClosePopup();
    },
    getItems() {
      let items = data.filter((row) => 
        row.value1 || row.value2 || row.value3 || row.value4
      );

      // on trie
      items = sortBy(
        items,
        (x) => x.value1,
        (x) => x.value2,
        (x) => x.value3,
        (x) => x.value4
      );

      return items.map((row) => {
        const values = [];

        if (row.value1) {
          values.push(row.value1);
        }

        if (row.value2) {
          values.push(row.value2);
        }

        if (row.value3) {
          values.push(row.value3);
        }

        if (row.value4) {
          values.push(row.value4);
        }

        return values;
      });
    },

    exportJson() {
      const items = this.getItems();
      const jsonString = JSON.stringify(items);

      this.downloadFile(jsonString, 'export.json', 'application/json');
    },

    exportText() {
      const items = this.getItems();
      const plainText = join(
        items.map((item) => {
          return join(item, '/');
        }),
        '\n'
      );

      this.downloadFile(plainText, 'export.txt', 'text/plain');
    },

    downloadFile(blobPart, filename, type) {
      const blob = new Blob([blobPart], { type });

      const blobUrl = URL.createObjectURL(blob);

      var link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      link.click();
    },
  }));

  const columns = React.useMemo(() => {
    const sortValueFn = (rowA, rowB, columnId) => {
      const a = rowA.original[columnId];
      const b = rowB.original[columnId];

      if (!a) {
        return 1;
      }

      if (!b) {
        return -1;
      }

      return a.localeCompare(b, lang, {
        ignorePunctuation: true,
        sensitivity: 'base',
      });
    };

    return [
      {
        id: 'select',
        header: ({ table }) => (
          <div className="checkbox">
            <IndeterminateCheckbox
              {...{
                checked: table.getIsAllRowsSelected(),
                indeterminate: table.getIsSomeRowsSelected(),
                onChange: table.getToggleAllRowsSelectedHandler(),
              }}
            />
          </div>
        ),
        cell: ({ row }) => (
          <div className="checkbox">
            <IndeterminateCheckbox
              {...{
                checked: row.getIsSelected(),
                disabled: !row.getCanSelect(),
                indeterminate: row.getIsSomeSelected(),
                onChange: row.getToggleSelectedHandler(),
              }}
            />
          </div>
        ),
      },
      {
        accessorKey: 'value1',
        header: () => (lang === 'fr' ? 'Valeur 1' : 'Value 1'),
        sortingFn: sortValueFn,
      },
      {
        accessorKey: 'value2',
        header: () => (lang === 'fr' ? 'Valeur 2' : 'Value 2'),
        sortingFn: sortValueFn,
      },
      {
        accessorKey: 'value3',
        header: () => (lang === 'fr' ? 'Valeur 3' : 'Value 3'),
        sortingFn: sortValueFn,
      },
      {
        accessorKey: 'value4',
        header: () => (lang === 'fr' ? 'Valeur 4' : 'Value 4'),
        sortingFn: sortValueFn,
      },
    ];
  }, [lang]);

  const table = useReactTable({
    data: data,
    columns,
    defaultColumn: {
      cell: (props) => <EditableCell {...props} />,
    },
    filterFns: {
      fuzzy: fuzzyFilter, //define as a filter function that can be used in column definitions
    },
    state: {
      globalFilter,
      // sorting,
    },
    onGlobalFilterChange: setGlobalFilter,
    // onSortingChange: setSorting,
    globalFilterFn: 'fuzzy',
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(), //provide a sorting row model

    enableColumnResizing: true,
    enableRowSelection: true,
    columnResizeMode: 'onChange',
    meta: {
      updateData: (rowIndex, columnId, value) => {
        const items = [...data];
        const row = items[rowIndex];
        row[columnId] = value;

        if (value !== '') {
          const itemExists = items.filter(
            (x) =>
              x.value1 === value ||
              x.value2 === value ||
              x.value3 === value ||
              x.value4 === value
          );

          // si le mot existe déjà
          if (itemExists.length > 1) {
            setPopup({
              display: true,
              title: '',
              text: {
                fr: 'La valeur existe déjà',
                en: 'The value already exists',
              },
              buttons: [],
              icon: null,
              showIcon: true,
            });

            row[columnId] = '';

            return;
          }
        }

        setData(items);

        // Eviter les cellules vides
        if (!row.value1 && !row.value2 && !row.value3 && !row.value4) {
          setPopup({
            display: true,
            title: '',
            text: {
              fr: 'La ligne est vide. Voulez-vous la supprimer ?',
              en: 'The line is empty. Do you want to delete it?',
            },
            buttons: [
              {
                text: {
                  fr: 'Oui',
                  en: 'Yes',
                },
                onclick: () => {
                  deleteItem(rowIndex);
                  onClosePopup();
                },
              },
              {
                text: {
                  fr: 'Non',
                  en: 'No',
                },
                onclick: onClosePopup,
              },
            ],
            onCancel: onClosePopup,
            icon: null,
            showIcon: true,
          });

          return;
        }
      },
    },
  });

  const { rows } = table.getRowModel();

  //The virtualizer needs to know the scrollable container element
  const tableContainerRef = React.useRef(null);

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    estimateSize: () => 33, //estimate row height for accurate scrollbar dragging
    getScrollElement: () => tableContainerRef.current,
    //measure dynamic row height, except in firefox because it measures table border height incorrectly
    measureElement:
      typeof window !== 'undefined' &&
      navigator.userAgent.indexOf('Firefox') === -1
        ? (element) => element?.getBoundingClientRect().height
        : undefined,
    overscan: 5,
  });

  return (
    <div className="container-erreurs">
      <div className="container-erreurs-header">
        <DebouncedInput
          id="txtRecherche"
          type="text"
          value={globalFilter ?? ''}
          onChange={(value) => setGlobalFilter(String(value))}
          placeholder={lang === 'fr' ? 'Rechercher' : 'Search'}
        />
      </div>

      <div className="container-erreurs-body" ref={tableContainerRef}>
        <div className="table">
          <table>
            <thead
              style={{
                display: 'grid',
                position: 'sticky',
                top: 0,
                zIndex: 1,
                backgroundColor: 'black',
              }}
            >
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th key={header.id}>
                      <div
                        {...{
                          className: header.column.getCanSort() ? 'sort' : '',
                          onClick: header.column.getToggleSortingHandler(),
                        }}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}

                        <SortIcon order={header.column.getIsSorted()} />
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody
              style={{
                display: 'grid',
                height: `${rowVirtualizer.getTotalSize()}px`, //tells scrollbar how big the table is
                position: 'relative', //needed for absolute positioning of rows
              }}
            >
              {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                const row = rows[virtualRow.index];

                return (
                  <tr
                    data-index={virtualRow.index} //needed for dynamic row height measurement
                    ref={(node) => rowVirtualizer.measureElement(node)} //measure dynamic row height
                    key={row.id}
                    style={{
                      display: 'flex',
                      position: 'absolute',
                      transform: `translateY(${virtualRow.start}px)`, //this should always be a `style` as it changes on scroll
                      width: '100%',
                    }}
                  >
                    {row.getVisibleCells().map((cell) => {
                      return (
                        <td key={cell.id}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="container-erreurs-footer">
        <p>
          {table.getRowCount()} {lang === 'fr' ? 'éléments' : 'items'}
        </p>
      </div>
      <div>
        <SimplePopup
          display={popup.display}
          title={popup.title}
          text={popup.text}
          onCancel={onClosePopup}
          buttons={popup.buttons}
          showIcon={popup.showIcon}
          icon={popup.icon}
        />
      </div>
    </div>
  );
});

export default BaseErreurs;
